import { format } from 'date-fns'
import type { FC } from 'react'
import type { ReactDatePickerCustomHeaderProps } from 'react-datepicker'

import { Body1, Body2 } from '@ui/Typography'
import { IconButton } from '@ui/components/Button'
import { ChevronIcon } from '@ui/icons/Chevron.icon'

import { useWindowResize } from '@utils/domUtils'

import { Box } from '@shared/components/Box'

import { ViewMode } from './useYearMonthNavigation'

interface CalendarHeaderProps extends ReactDatePickerCustomHeaderProps {
  mode: ViewMode
  onModeChange?: (mode: ViewMode) => void
  locale: Locale
}

export const CalendarHeader: FC<CalendarHeaderProps> = (props) => {
  const {
    date,
    monthDate,
    decreaseMonth,
    increaseMonth,
    decreaseYear,
    increaseYear,
    prevMonthButtonDisabled,
    prevYearButtonDisabled,
    nextMonthButtonDisabled,
    nextYearButtonDisabled,
    mode,
    onModeChange,
    visibleYearsRange,
    locale,
  } = props
  const displayDate = monthDate || date
  const isMobile = useWindowResize()

  const label =
    mode === 'year'
      ? format(displayDate, 'yyyy', { locale })
      : mode === 'month'
      ? format(displayDate, 'yyyy', { locale })
      : format(displayDate, 'LLLL yyyy', { locale })

  const handleLabelClick = () => {
    if (!onModeChange) return

    if (mode === 'day') {
      onModeChange('month')
    } else if (mode === 'month') {
      onModeChange('year')
    } else {
      onModeChange('day')
    }
  }

  const handlePrev = () => {
    if (mode === 'day') {
      decreaseMonth()
    } else {
      decreaseYear()
    }
  }

  const handleNext = () => {
    if (mode === 'day') {
      increaseMonth()
    } else {
      increaseYear()
    }
  }

  const isPrevDisabled = mode === 'year' ? prevYearButtonDisabled : prevMonthButtonDisabled
  const isNextDisabled = mode === 'year' ? nextYearButtonDisabled : nextMonthButtonDisabled
  const yearsRange =
    visibleYearsRange && visibleYearsRange?.startYear && visibleYearsRange?.endYear
      ? `${visibleYearsRange.startYear}-${visibleYearsRange.endYear}`
      : null
  const Title = isMobile ? Body1 : Body2

  return (
    <Box flex justify='space-between' align='center'>
      <IconButton.Ghost
        icon={ChevronIcon.Left}
        onClick={handlePrev}
        disabled={isPrevDisabled}
        size={isMobile ? 'lg' : 'sm'}
      />

      <Box
        onClick={handleLabelClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleLabelClick()
          }
        }}
        py='x6'
        px='x12'
        cursor='pointer'
        role='button'
        tabIndex={0}
        aria-label='Change calendar view'
      >
        <Title medium>
          {yearsRange ? yearsRange : label.charAt(0).toUpperCase() + label.slice(1)}
        </Title>
      </Box>

      <IconButton.Ghost
        icon={ChevronIcon.Right}
        onClick={handleNext}
        disabled={isNextDisabled}
        size={isMobile ? 'lg' : 'sm'}
      />
    </Box>
  )
}
